import { useState, useCallback, useRef, useEffect } from 'react'
import { api, ApiError } from '../utils/api'
import { getVoteType, addVotedChoice, removeVotedChoice, hasVotedForChoice } from '../utils/api'
import logger from '../utils/logger'
import { toast } from 'react-hot-toast'
import { trackVoteCast, trackVoteRemoved, trackErrorVote, trackErrorVoteRemove } from '../utils/analytics'

/**
 * Custom hook for managing vote operations and optimistic updates
 * @param {string} pollId - The poll ID
 * @param {Array} choices - Current choices array
 * @param {Function} setChoices - Function to update choices
 * @param {string} deviceId - Current device ID
 * @param {Object} pendingVotesRef - Reference to pending votes set
 * @param {Object} pendingRemovalsRef - Reference to pending removals set
 * @returns {Object} Hook state and functions
 */
function useVoteManagement(pollId, choices, setChoices, deviceId, pendingVotesRef, pendingRemovalsRef) {
  const [votingStates, setVotingStates] = useState({})

  // Use a ref to access the latest choices inside callbacks without adding them as dependencies
  const choicesRef = useRef(choices)

  // Keep the ref synced with the latest choices
  useEffect(() => {
    choicesRef.current = choices
  }, [choices])

  const handleVote = useCallback(async (choiceId, voteType = 'upvote') => {
    const currentVoteType = getVoteType(choiceId)
    // Use ref to get choices without dependency
    const choice = choicesRef.current.find(c => c.id === choiceId)

    if (!choice) {
      logger.warn('Choice not found for voting', { choiceId })
      return
    }

    // Determine what action to take
    let action = 'create'
    if (currentVoteType === voteType) {
      // Same vote type - remove vote (toggle off)
      action = 'remove'
    } else if (currentVoteType) {
      // Different vote type - toggle
      action = 'toggle'
    }

    setVotingStates(prev => ({ ...prev, [choiceId]: voteType }))

    // Track this vote as pending BEFORE API call to prevent race condition with real-time events
    pendingVotesRef.current.add(choiceId)

    // Store original counts for reverting if needed
    const originalUpvotes = choice.upvote_count || 0
    const originalDownvotes = choice.downvote_count || 0
    const originalNetVotes = choice.vote_count || 0

    // Optimistically update the UI immediately (before API call)
    setChoices(prev => {
      const updated = prev.map(c => {
        if (c.id !== choiceId) return c

        let newUpvotes = c.upvote_count || 0
        let newDownvotes = c.downvote_count || 0

        if (action === 'remove') {
          // Remove vote
          if (voteType === 'upvote') {
            newUpvotes = Math.max(0, newUpvotes - 1)
          } else {
            newDownvotes = Math.max(0, newDownvotes - 1)
          }
        } else if (action === 'toggle') {
          // Toggle vote type
          if (currentVoteType === 'upvote') {
            newUpvotes = Math.max(0, newUpvotes - 1)
            newDownvotes = (newDownvotes || 0) + 1
          } else {
            newDownvotes = Math.max(0, newDownvotes - 1)
            newUpvotes = (newUpvotes || 0) + 1
          }
        } else {
          // Create new vote
          if (voteType === 'upvote') {
            newUpvotes = (newUpvotes || 0) + 1
          } else {
            newDownvotes = (newDownvotes || 0) + 1
          }
        }

        const newNetVotes = newUpvotes - newDownvotes

        return {
          ...c,
          upvote_count: newUpvotes,
          downvote_count: newDownvotes,
          vote_count: newNetVotes
        }
      })

      // Re-sort after vote
      updated.sort((a, b) => {
        if (b.vote_count !== a.vote_count) return b.vote_count - a.vote_count
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      })

      return updated
    })

    const startTime = performance.now()
    try {
      logger.info('Casting vote', { pollId, choiceId, deviceId, voteType, action })

      const response = await api.post(`/api/choices/${choiceId}/votes`, {
        device_id: deviceId,
        vote_type: voteType
      })

      logger.info('Vote processed successfully', {
        pollId,
        choiceId,
        deviceId,
        voteType,
        action,
        response,
        durationMs: performance.now() - startTime
      })

      // Track analytics event for successful votes
      if (action === 'remove') {
        trackVoteRemoved(pollId, choiceId)
      } else {
        trackVoteCast(pollId, choiceId, voteType)
      }

      // Update localStorage based on action - this must happen after successful API call
      if (action === 'remove') {
        removeVotedChoice(choiceId)
        logger.debug('localStorage: Removed vote after toggle off', { pollId, choiceId })
      } else {
        addVotedChoice(choiceId, voteType)
        logger.debug('localStorage: Updated vote type', { pollId, choiceId, voteType, action })

        // Verify localStorage was updated correctly
        const storedType = getVoteType(choiceId)
        if (storedType !== voteType) {
          logger.warn('localStorage: Vote type mismatch after update, fixing', {
            pollId,
            choiceId,
            expected: voteType,
            stored: storedType
          })
          addVotedChoice(choiceId, voteType) // Force update
        }
      }

      // Update counts from server response
      if (response && typeof response.upvote_count === 'number') {
        setChoices(prev => {
          const updated = prev.map(c =>
            c.id === choiceId
              ? {
                ...c,
                upvote_count: response.upvote_count,
                downvote_count: response.downvote_count,
                vote_count: response.vote_count
              }
              : c
          )

          // Re-sort after updating with server counts
          updated.sort((a, b) => {
            if (b.vote_count !== a.vote_count) return b.vote_count - a.vote_count
            return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          })

          return updated
        })
      }

      // Remove from pending set after a short delay to allow real-time event to arrive
      setTimeout(() => {
        pendingVotesRef.current.delete(choiceId)
      }, 2000)

    } catch (err) {
      // Remove from pending set since vote failed
      pendingVotesRef.current.delete(choiceId)

      // Revert optimistic update
      setChoices(prev => {
        const updated = prev.map(c =>
          c.id === choiceId
            ? {
              ...c,
              upvote_count: originalUpvotes,
              downvote_count: originalDownvotes,
              vote_count: originalNetVotes
            }
            : c
        )

        // Re-sort after reverting vote
        updated.sort((a, b) => {
          if (b.vote_count !== a.vote_count) return b.vote_count - a.vote_count
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        })

        return updated
      })

      let errorMessage = 'Failed to cast vote. Please try again.'

      if (err instanceof ApiError) {
        errorMessage = err.message
      }

      logger.error('Failed to cast vote', {
        pollId,
        choiceId,
        deviceId,
        voteType,
        error: errorMessage,
        durationMs: performance.now() - startTime
      })
      trackErrorVote(pollId, choiceId, errorMessage)
      toast.error(errorMessage)
    } finally {
      setVotingStates(prev => ({ ...prev, [choiceId]: false }))
    }
  }, [pollId, setChoices, deviceId, pendingVotesRef])

  const handleToggleVote = useCallback(async (choiceId) => {
    const currentVoteType = getVoteType(choiceId)
    if (!currentVoteType) {
      logger.warn('Cannot toggle vote: no vote exists', { choiceId })
      return
    }

    const newVoteType = currentVoteType === 'upvote' ? 'downvote' : 'upvote'
    await handleVote(choiceId, newVoteType)
  }, [handleVote])

  const handleRemoveVote = useCallback(async (choiceId) => {
    // Check if user has voted for this choice
    if (!hasVotedForChoice(choiceId)) {
      logger.warn('Cannot remove vote: user has not voted for this choice', { choiceId })
      return
    }

    const voteType = getVoteType(choiceId)
    // Use ref to get choices without dependency
    const choice = choicesRef.current.find(c => c.id === choiceId)

    if (!choice) {
      logger.warn('Choice not found for vote removal', { choiceId })
      return
    }

    setVotingStates(prev => ({ ...prev, [choiceId]: 'remove' }))

    // Track this removal as pending BEFORE API call to prevent race condition with real-time events
    pendingRemovalsRef.current.add(choiceId)

    // Store the current counts for reverting if needed
    const originalUpvotes = choice.upvote_count || 0
    const originalDownvotes = choice.downvote_count || 0
    const originalNetVotes = choice.vote_count || 0

    // Optimistically update the UI immediately (before API call)
    setChoices(prev => {
      const updated = prev.map(c => {
        if (c.id !== choiceId) return c

        let newUpvotes = c.upvote_count || 0
        let newDownvotes = c.downvote_count || 0

        // Remove vote based on vote type
        if (voteType === 'upvote') {
          newUpvotes = Math.max(0, newUpvotes - 1)
        } else {
          newDownvotes = Math.max(0, newDownvotes - 1)
        }

        const newNetVotes = newUpvotes - newDownvotes

        return {
          ...c,
          upvote_count: newUpvotes,
          downvote_count: newDownvotes,
          vote_count: newNetVotes
        }
      })

      // Re-sort after vote removal
      updated.sort((a, b) => {
        if (b.vote_count !== a.vote_count) return b.vote_count - a.vote_count
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      })

      return updated
    })

    const startTime = performance.now()
    try {
      logger.info('Removing vote', { pollId, choiceId, deviceId, voteType })

      const response = await api.delete(`/api/choices/${choiceId}/votes`, {
        device_id: deviceId
      })

      logger.info('Vote removed successfully', {
        pollId,
        choiceId,
        deviceId,
        response,
        durationMs: performance.now() - startTime
      })

      // Remove this choice from voted list in localStorage immediately
      removeVotedChoice(choiceId)
      trackVoteRemoved(pollId, choiceId)

      // Update counts from server response
      if (response && typeof response.upvote_count === 'number') {
        logger.info('Updating vote counts from server response', { pollId, choiceId, response })
        setChoices(prev => {
          const updated = prev.map(c =>
            c.id === choiceId
              ? {
                ...c,
                upvote_count: response.upvote_count,
                downvote_count: response.downvote_count,
                vote_count: response.vote_count
              }
              : c
          )

          // Re-sort after updating with server counts
          updated.sort((a, b) => {
            if (b.vote_count !== a.vote_count) return b.vote_count - a.vote_count
            return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          })

          return updated
        })
      } else {
        logger.warn('Server response missing vote counts, keeping optimistic update', {
          pollId,
          choiceId,
          response
        })
      }

      // Remove from pending set after a short delay to allow real-time event to arrive
      setTimeout(() => {
        pendingRemovalsRef.current.delete(choiceId)
      }, 2000)

    } catch (err) {
      // Remove from pending set since removal failed
      pendingRemovalsRef.current.delete(choiceId)

      // Revert optimistic update
      setChoices(prev => {
        const updated = prev.map(c =>
          c.id === choiceId
            ? {
              ...c,
              upvote_count: originalUpvotes,
              downvote_count: originalDownvotes,
              vote_count: originalNetVotes
            }
            : c
        )

        // Re-sort after reverting removal
        updated.sort((a, b) => {
          if (b.vote_count !== a.vote_count) return b.vote_count - a.vote_count
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        })

        return updated
      })

      let errorMessage = 'Failed to remove vote. Please try again.'

      if (err instanceof ApiError) {
        if (err.code === 'NOT_FOUND') {
          // Vote doesn't exist - remove from localStorage anyway
          removeVotedChoice(choiceId)
          errorMessage = 'Vote not found'
          logger.warn('Server confirmed vote not found', { choiceId, deviceId })
        } else {
          errorMessage = err.message
        }
      }

      logger.error('Failed to remove vote', {
        pollId,
        choiceId,
        deviceId,
        error: errorMessage,
        durationMs: performance.now() - startTime
      })
      trackErrorVoteRemove(pollId, choiceId, errorMessage)
      toast.error(errorMessage)
    } finally {
      setVotingStates(prev => ({ ...prev, [choiceId]: false }))
    }
  }, [pollId, setChoices, deviceId, pendingRemovalsRef])

  return {
    votingStates,
    handleVote,
    handleToggleVote,
    handleRemoveVote
  }
}

export default useVoteManagement
